import React from 'react';
import { formatDate, formatDateDayMonth } from '../utils/dateFormat';
import { BTN_XS } from '../constants/buttons';

/**
 * The received-tick controls for LINE-ITEM ROWS (2026-08-15).
 *
 * Product owner: *"I would like the tick boxes to go in the collpased line
 * items in the 'Line items' section maybe between the price and the actions
 * menu so they can be ticked off without haveing to expand the order. This
 * will save the whole Items received section. THe title of the 'Line Items'
 * section could be renamed once orders have been repalced to
 * 'Line Items - received (1/2)' for example"* ("repalced" = PLACED — the
 * delivery-stage statuses).
 *
 * This file REPLACES the standalone "Items received" block that
 * `OrderExpandedDetails.js` rendered between Attachments and Delivery
 * Paperwork (:445-528 at 46c58052). The per-line step and the header's
 * next-step button render INSIDE `OrderForm`'s collapsed item headers and its
 * `Line Items` section header (optional `received` prop) — the ONLY surface
 * since the read-only tables were folded into `OrderForm` (both branches of
 * `OrderExpandedRow`, and `pages/OrderPage.js`). `Approvals.js` needs none:
 * every row there is `submitted`, which is not a delivery-stage status.
 *
 * 🔀 2026-10-28 — PER-LINE "ORDERED" THEN "DELIVERED" (owner: *"one then the
 * other in squence"*; ruling A: a line may be delivered as soon as THAT line
 * is ordered). Each line carries TWO on/off controls, Ordered and Delivered
 * (`orderedControlState` / `deliveredControlState`; the visual lives in ONE
 * local `LineFlagControl`); the tri-state tick-all is DELETED for one
 * next-step button (`headerNextStep`).
 *
 * The checkbox class string is carried over verbatim from the retired block —
 * same size, same green, same focus ring — so the control the owner has
 * already seen does not change appearance, only address.
 *
 * 🔴 NOTHING HERE DECIDES WHO. The PAGE resolves `canReceiveItems`
 * (mirroring the server's received gate: isGlobalAdmin ||
 * canManageDepartmentOrders(order's OWN dept) || isOwner || isStores), the
 * NARROWER `canOrderItems` (`canManageOrder` — the server's
 * `requireOrderManagement`), and the delivery-stage predicate
 * (`DELIVERY_STAGE_STATUSES.includes(status)`), and only renders these when
 * they hold. Same
 * contract as `OrderTableView` / `OrderActionsMenu` / `OrderForm`: finished,
 * already-gated nodes travel down; no permission is re-derived below page
 * level.
 *
 * 🪤 BOTH CONTROLS STOP PROPAGATION ON CLICK **AND** KEYDOWN. Every home they
 * have sits inside something clickable — `OrderForm`'s item header is a
 * `role="button"` that toggles collapse on click and on Enter/Space, and the
 * form's section header sits beside a collapse-all button — so without the
 * keydown a keyboard user ticking a box would also collapse the item under
 * it. The same pair the `OrderItemActionsMenu` wrapper carries, for the same
 * recorded reason. Harmless in the read-only tables, where the ancestors do
 * not listen.
 *
 * 📅 THE RECEIVED DATE DEMOTED TO A TOOLTIP ON DESKTOP — an accepted trade,
 * not an oversight. The retired block gave every received item an inline
 * "Received 12 Aug 26" text line; a table cell and a 39px collapsed header
 * cannot afford one. The date survives visibly in the mobile cards (the
 * caller passes it through `labelText`) and in each checkbox's `title`; if
 * the owner misses an always-visible desktop date, re-add it in the read-only
 * tables rather than growing the collapsed header.
 */

// Verbatim from the retired "Items received" block — the one thing about the
// control the owner has already approved on screen.
export const RECEIVED_CHECKBOX_CLASS =
  'w-4 h-4 text-green-600 bg-white border-gray-300 rounded focus:ring-green-500 cursor-pointer';

/**
 * THE VISIBLE LABELS OF THE PER-LINE STEP — one home.
 *
 * 🔀 2026-10-28 (per-line "ordered"): the line shows ONE step at a time, so
 * the md+ "Mark as delivered" and the section's tri-state "Mark all as
 * delivered" tick (`ReceiveAllCheckbox`) are DELETED with the tick-all —
 * the header is a button naming the next step (`headerNextStepLabel`). The
 * step labels follow the approved mock: "Ordered" then "Delivered".
 *
 * 🪤 The `title`/`aria-label` of `ItemReceivedCheckbox` still say
 * "received" (the 2026-08-15 wording split, the owner's call).
 */
export const ITEM_RECEIVED_SHORT_LABEL = 'Delivered';

/**
 * The section title once an order is approved-or-later: the owner's rename,
 * with BOTH counts since the per-line "ordered" step (2026-10-28, approved
 * mock): "Line Items — ordered 3/5 · delivered 1/5". Em dash per the
 * confirmed 2026-08-15 design.
 */
export function lineItemsReceivedTitle(orderedCount, receivedCount, total) {
  return `Line Items — ordered ${orderedCount}/${total} · delivered ${receivedCount}/${total}`;
}

/** 📱 The same title below `md`, ONE line at 346px beside the header button
 *  (owner, 2026-09-30): only the stage in progress — "Items · 3/5 ordered"
 *  while a line is still to order, then "Items · 1/5 delivered". 🔀 2026-10-28
 *  phone audit: "Line Items · …", the same section name My Orders' plain
 *  "Line Items · N" uses (it truncates if it ever runs out of room). */
export function lineItemsReceivedShortTitle(orderedCount, receivedCount, total) {
  return orderedCount < total
    ? `Line Items · ${orderedCount}/${total} ordered`
    : `Line Items · ${receivedCount}/${total} delivered`;
}

// ── PER-LINE "ORDERED" AND "DELIVERED" (owner, 2026-10-28) ─────────────────
// Ruling A: a line may be delivered as soon as THAT line is ordered. Second
// ruling the same day, after mocks: TWO independent on/off controls per line —
// *"orderd and delivered can be on/off toggles, delivered greyed out untill
// ordered is on"* — no ↺, the date beside an "on" control, who in the title
// only (*"do we need the additional who information? it just takes up space.
// Maybe the date MAX"*). These pure rules are the whole decision; the
// component below only draws them.

/** "28 Oct by Mason Kowalski" — either half optional. */
function whenWho(date, name) {
  const when = date ? formatDateDayMonth(date) : '';
  return [when, name ? `by ${name}` : ''].filter(Boolean).join(' ');
}

/**
 * The ORDERED control of one line. ON when the line is ordered (a received
 * line is ordered — the server enforces is_received ⇒ is_ordered). LOCKED while
 * Delivered is on (the server refuses unordering a received line,
 * `line_already_received`); greyed for a viewer who may not order (`canOrder`
 * false — Stores, the requester), with the page's reason. Switching it OFF is
 * the "it was a mistake" path (the server's unorder).
 */
export function orderedControlState(line, { canOrder = false, orderLockedReason = null } = {}) {
  const on = !!line && (!!line.is_ordered || !!line.is_received);
  if (line && line.is_received) {
    return { on, disabled: true, title: 'Delivered — switch Delivered off first' };
  }
  if (!canOrder) {
    return { on, disabled: true, title: orderLockedReason || 'Only someone who manages this department\'s orders can change this' };
  }
  const stamp = on ? whenWho(line.ordered_date, line.ordered_by_name) : '';
  return {
    on,
    disabled: false,
    title: on ? `Ordered${stamp ? ` ${stamp}` : ''}` : 'Click when it is ordered from the supplier',
  };
}

/**
 * The DELIVERED control of one line. Greyed until Ordered is on ("Mark it
 * Ordered first" — the server's `line_not_ordered`); otherwise live for any
 * viewer the page let receive. Switching it off un-receives the line.
 */
export function deliveredControlState(line) {
  const on = !!line && !!line.is_received;
  const ordered = !!line && (!!line.is_ordered || on);
  if (!ordered) return { on: false, disabled: true, title: 'Mark it Ordered first' };
  const stamp = on ? whenWho(line.received_date, line.received_by_name) : '';
  return { on, disabled: false, title: on ? `Delivered${stamp ? ` ${stamp}` : ''}` : 'Click when it arrives' };
}

/** The section header's ONE button: the next step for every line at that
 *  stage. "Mark all ordered (n)" while a line is unordered AND the viewer may
 *  order; otherwise "Mark all delivered (n)" for the ordered, undelivered
 *  lines (a Stores/owner viewer, who may receive but not order, gets this
 *  even while other lines wait to be ordered — receive-all only receives
 *  ORDERED lines). Null when nothing is left for this viewer. */
export function headerNextStep({ orderedCount, receivedCount, total, canOrder }) {
  const toOrder = total - orderedCount;
  if (canOrder && toOrder > 0) return { kind: 'order', count: toOrder };
  const toDeliver = orderedCount - receivedCount;
  if (toDeliver > 0) return { kind: 'deliver', count: toDeliver };
  return null;
}

export function headerNextStepLabel(step) {
  if (!step) return '';
  return step.kind === 'order'
    ? `Mark all ordered (${step.count})`
    : `Mark all delivered (${step.count})`;
}

/** The order list bar's words (approved mock): "3 ordered of 5",
 *  "4 ordered · 1 delivered of 4", "3 delivered of 3". Colour is never the
 *  sole carrier of the two-tone bar. */
export function lineProgressLabel(orderedCount, receivedCount, total) {
  if (total > 0 && receivedCount >= total) return `${receivedCount} delivered of ${total}`;
  if (receivedCount === 0) return `${orderedCount} ordered of ${total}`;
  return `${orderedCount} ordered · ${receivedCount} delivered of ${total}`;
}

/**
 * The `received` prop's `items` map — `{ [order_items.id]: line step state }`
 * from the DETAIL payload. ONE builder for the expanded row and the order page
 * (it was two copies of the same reduce — duplication group 7e7f5cea, closed
 * 2026-10-28). Unfiltered on purpose: an adjustment line still renders and the
 * server refuses its tick (`item_not_receivable` / `item_not_orderable`).
 */
export function lineStepMap(items) {
  return (Array.isArray(items) ? items : []).reduce((acc, it) => {
    if (it && it.id != null) {
      acc[it.id] = {
        is_received: !!it.is_received,
        received_date: it.received_date,
        received_by_name: it.received_by_name,
        is_ordered: !!it.is_ordered,
        ordered_date: it.ordered_date,
        ordered_by_name: it.ordered_by_name,
      };
    }
    return acc;
  }, {});
}

export const ITEM_ORDERED_LABEL = 'Ordered';


const stop = (e) => e.stopPropagation();

/**
 * One line item's received tick.
 *
 * - `labelText` (mobile cards, and — since the 2026-08-15 header-label fix —
 *   the form's item header, where it is a `hidden md:inline`
 *   "Mark as delivered"): visible text beside the checkbox — the caller
 *   supplies the text and its classes, so the mobile cards keep the received
 *   date visible below `md` while the form header shows its label only at
 *   `md`+. Living inside the checkbox's <label>, the text exists exactly when
 *   the tick does, toggles it on click, and shares its stopPropagation.
 * - Without `labelText` (the read-only tables): checkbox only, in a hit-area
 *   label that holds the 44px tap-target floor below `md` and collapses to
 *   the bare 16px box at `md` and up, so the 39px desktop collapsed row does
 *   not grow.
 */
export function ItemReceivedCheckbox({
  checked,
  receivedDate = null,
  disabled = false,
  onChange,
  testId,
  ariaLabel,
  labelText = null,
  labelClassName = '',
  className = '',
  // Overrides the computed title when the control is disabled for a REASON
  // the caller wants named (2026-08-16, the "grey out until Ordered" gate) —
  // e.g. "Mark this order as Ordered before items can be marked delivered."
  // Ignored while `disabled` is false, so a busy-only disable (an in-flight
  // mutation) keeps today's title untouched. Default null → byte-identical
  // to before for every caller that doesn't pass it.
  disabledReason = null,
  // A caller-owned tooltip that replaces the received wording outright
  // (2026-09-29 — the hires pages' per-line "Off hired" tick reuses this
  // checkbox's geometry and tap target, and is not about receiving at all).
  // Default null → byte-identical to before for every caller that omits it.
  title: titleOverride = null,
}) {
  const title = titleOverride != null ? titleOverride : (disabled && disabledReason)
    ? disabledReason
    : checked
      ? `Received${receivedDate ? ` ${formatDate(receivedDate)}` : ''} — click to mark as not received`
      : 'Mark as received';
  return (
    <label
      className={
        className
        || (labelText
          ? 'flex items-center justify-between gap-2 min-h-[44px] cursor-pointer'
          : 'flex items-center justify-center min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 cursor-pointer')
      }
      title={title}
      onClick={stop}
      onKeyDown={stop}
    >
      {labelText != null && <span className={labelClassName}>{labelText}</span>}
      <input
        type="checkbox"
        checked={!!checked}
        onChange={onChange}
        disabled={disabled}
        className={RECEIVED_CHECKBOX_CLASS}
        data-testid={testId}
        aria-label={ariaLabel}
      />
    </label>
  );
}

/**
 * 🔁 THE ONE PLACE THE ON/OFF VISUAL LIVES (2026-10-28) — the owner's
 * "Round checks" (option 4 of his mock): a 16px circle, a 1.5px outline when
 * off and a green-600 fill with a white ✓ when on, followed by the word, which
 * is part of the control. Date and who live in the `title` only.
 *
 * A real native checkbox (visually hidden, `peer`) inside its <label>, so
 * keyboard, `checked`/`disabled` and the accessible name are the platform's;
 * the circle is a `peer-*`-styled span. `relative` on the label because an
 * `sr-only` child of an `overflow-x-auto` needs a positioned ancestor (root
 * CLAUDE.md, Styling Patterns). 44px tap target below `md`.
 *
 * 🎨 Outline measured 2026-10-28 against the row in both themes (WCAG 1.4.11
 * 3:1, the circle is the control's boundary): gray-500 on white 4.83:1;
 * gray-400 on the dark row (#1e293b, index.css's
 * `html.dark .bg-white`) 5.76:1 (4.06:1 on a gray-700 hover); the on-fill
 * green-600 3.30:1 on white, 4.44:1 dark; the white ✓ on green-600 3.30:1.
 * Neither token is in index.css's `html.dark … !important` tables, so the
 * `dark:` pair is live.
 */
function LineFlagControl({ state, label, onToggle, busy, locked, lockedReason, testId, ariaLabel }) {
  const disabled = state.disabled || busy || locked;
  const title = locked && lockedReason ? lockedReason : state.title;
  return (
    <label
      // 📱 Phone hit area (owner 2026-10-28: "keep the tap size as small as practical"):
      // an INVISIBLE ::before 8px above and below (≈32px tall, over WCAG 2.5.8's 24px floor)
      // and 4px each side, so the row does not grow; the 12px phone gap keeps neighbours apart.
      className={`relative inline-flex items-center justify-center gap-1.5 max-md:before:absolute max-md:before:content-[''] max-md:before:-inset-y-2 max-md:before:-inset-x-1 text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
      title={title}
      onClick={stop}
      onKeyDown={stop}
    >
      <input
        type="checkbox"
        className="sr-only peer"
        checked={!!state.on}
        disabled={disabled}
        onChange={() => { if (!disabled) onToggle(!!state.on); }}
        data-testid={testId}
        aria-label={ariaLabel}
      />
      <span
        aria-hidden="true"
        data-testid={`${testId}-circle`}
        data-tick-circle={state.on ? 'on' : 'off'}
        className={`inline-flex items-center justify-center w-4 h-4 rounded-full border-[1.5px] text-[10px] leading-none peer-focus-visible:ring-2 peer-focus-visible:ring-green-500 peer-focus-visible:ring-offset-1 ${state.on ? 'bg-green-600 border-green-600 text-white dark:bg-green-600 dark:border-green-600' : 'border-gray-500 dark:border-gray-400 text-transparent'}`}
      >
        ✓
      </span>
      <span>{label}</span>
    </label>
  );
}

/**
 * ONE line's two controls (2026-10-28): Ordered and Delivered, each on/off.
 * `orderedControlState` / `deliveredControlState` decide every enabled /
 * disabled / title; the page decides WHO (`canOrder`, and the `received` prop's
 * presence for receiving). No ↺ — un-ticking IS the undo.
 */
export function LineOrderedDeliveredControls({
  line,
  canOrder = false,
  busy = false,
  locked = false,
  lockedReason = null,
  orderLockedReason = null,
  onToggleOrdered,
  onToggleReceived,
  testIdSuffix,
  itemLabel,
}) {
  if (!line) return null;
  const ordered = orderedControlState(line, { canOrder, orderLockedReason });
  const delivered = deliveredControlState(line);
  return (
    <span className="flex items-center gap-3 md:gap-4">
      <LineFlagControl
        state={ordered}
        label={ITEM_ORDERED_LABEL}
        onToggle={onToggleOrdered}
        busy={busy}
        locked={locked}
        lockedReason={lockedReason}
        testId={`order-form-item-ordered-${testIdSuffix}`}
        ariaLabel={`${itemLabel} ordered`}
      />
      <LineFlagControl
        state={delivered}
        label={ITEM_RECEIVED_SHORT_LABEL}
        onToggle={onToggleReceived}
        busy={busy}
        locked={locked}
        lockedReason={lockedReason}
        testId={`order-form-item-received-${testIdSuffix}`}
        ariaLabel={`${itemLabel} received`}
      />
    </span>
  );
}

/**
 * The `Line Items` section header's ONE button — the next step for every
 * line at that stage (`headerNextStep`). Renders nothing when there is none.
 * A BTN_XS.confirm button, stopping propagation like the ticks (the bar it
 * sits in folds the list on click).
 */
export function NextStepAllButton({ step, disabled = false, onOrderAll, onReceiveAll, testId }) {
  if (!step) return null;
  return (
    <button
      type="button"
      className={`${BTN_XS.confirm} whitespace-nowrap shrink-0 min-h-[44px] md:min-h-0`}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); if (step.kind === 'order') onOrderAll(true); else onReceiveAll(true); }}
      onKeyDown={stop}
      data-testid={testId}
      data-step={step.kind}
    >
      {headerNextStepLabel(step)}
    </button>
  );
}
